import React, { useCallback, useEffect, useMemo, useState } from 'react';
import lodash from 'lodash';
import { Redirect } from 'react-router-dom';

import initialFormData from 'constants/initialFormData';

import { EMPLOYEE_PROFILE } from 'constants/routes';
import { DATE_FORMAT_DEFAULT } from 'constants/appConstants';
import { USER_UPDATABLE_FIELDS } from 'constants/updatableFields';
import { EMPLOYEES, USER_PERMISSIONS } from 'constants/permissions';

import * as toast from 'utils/toast';
import { interpolate, extractFullName } from 'utils/string';
import { getFormattedDate } from 'utils/date';
import { handleError } from 'utils/errorHandler';

import {
  useDepartmentsQuery,
  useDesignationAreasQuery,
  useDesignationsQuery,
  useEngagementStatusesQuery,
} from 'hooks/query/useLookupQueries';
import {
  useFetchUserByIdQuery,
  useFetchUserRolesQuery,
  useUpdateUserMutation,
  useUploadUserImageMutation,
} from 'hooks/query/useUserQueries';
import { useRolesQuery } from 'hooks/query/useRoles';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import { filterAllowedRoles } from 'utils/user';
import Loading from 'components/common/loading/Loading';
import EmployeeForm from '../components/employeeForm/EmployeeForm';

import type {
  AppraiserAllocation,
  CreateUpdateForm,
  LeapfrogHistory,
  ProfileInfo,
  UserPayloadInput,
} from 'interface/createUpdateForm';
import type { Role } from 'interface/role';

interface PermissionRole {
  id: string;
  name?: string;
  [key: string]: string | number | boolean | undefined;
}

interface AvailabilityTimeValue {
  value?: string | null;
  type?: string | null;
}

interface EmployeeHistoryItem {
  id?: number;
  transitionDate?: string | Date | null;
}

interface EmployeeImagePreview {
  data_url: string;
  file: {
    name: string;
  };
}

interface EmployeeWithProfile extends Record<string, string | number | boolean | null | undefined | object> {
  id: number;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  email?: string;
  avatarUrl?: string | null;
  supervisor?: { id?: number | null } | null;
  leaveIssuer?: { id?: number | null } | null;
  teamManager?: { id?: number | null } | null;
  coach?: { id?: number | null } | null;
  department?: { id?: number | null } | null;
  roles?: Role[];
  pastExperience?: number | null;
  availabilityTime?: {
    startTime?: string | null;
    startTimeType?: string | null;
    endTime?: string | null;
    endTimeType?: string | null;
  };
  empStatusHistory?: EmployeeHistoryItem[];
  designationAreaHistory?: EmployeeHistoryItem[];
}

interface RedirectTarget {
  pageUrl: string;
  params: {
    id: number | null;
  };
}

interface PermissionsMap {
  [key: string]: boolean;
}

interface CurrentUser {
  id: number;
}

interface UpdateFormOwnProps {
  empId: number | string;
  user: CurrentUser;
}

type UpdateFormProps = UpdateFormOwnProps;
type EmployeeFormValues = CreateUpdateForm & ProfileInfo & LeapfrogHistory & AppraiserAllocation;
type FormDataShape = {
  employeeImage?: EmployeeImagePreview[] | Array<{ file?: File }> | null;
  availabilityStartTime?: AvailabilityTimeValue | { hours: number; minutes: number };
  availabilityEndTime?: AvailabilityTimeValue | { hours: number; minutes: number };
  joinDate?: string | Date | null;
  username?: string | null;
  roleIds?: number[];
  pastExperienceYears?: number | null;
  pastExperienceMonths?: number | null;
  supervisorId?: number | null;
  leaveIssuerId?: number | null;
  teamManagerId?: number | null;
  coachId?: number | null;
  departmentId?: number | null;
  empStatusHistory?: EmployeeHistoryItem[];
  designationAreaHistory?: EmployeeHistoryItem[];
  middleName?: string | null;
  [key: string]:
    | string
    | Date
    | number
    | boolean
    | null
    | undefined
    | AvailabilityTimeValue
    | number[]
    | EmployeeImagePreview[]
    | EmployeeHistoryItem[]
    | Array<{ file?: File }>
    | { hours: number; minutes: number };
};

const UpdateForm: React.FC<UpdateFormProps> = ({ empId, user }) => {
  const permissions = useUserPermissions() as PermissionsMap;
  const { data: employeeData, isLoading: isLoadingEmployee } = useFetchUserByIdQuery<EmployeeWithProfile>(empId);
  const { data: userRolesResponse, isLoading: isLoadingUserRoles } = useFetchUserRolesQuery<{
    roles?: Array<{ id?: number | string; name?: string }>;
  }>(empId);
  const { data: departments = [], isLoading: isLoadingDepartments } = useDepartmentsQuery();
  const { data: designations = [], isLoading: isLoadingDesignations } = useDesignationsQuery();
  const { data: designationAreas = [], isLoading: isLoadingDesignationAreas } = useDesignationAreasQuery();
  const { data: engagementStatus = [], isLoading: isLoadingEngagementStatus } = useEngagementStatusesQuery();
  const { data: allRoles = [], isLoading: isLoadingRoles } = useRolesQuery();
  const updateUserMutation = useUpdateUserMutation();
  const uploadUserImageMutation = useUploadUserImageMutation();

  const [employees, setEmployees] = useState<EmployeeWithProfile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [toPage, setToPage] = useState<RedirectTarget | null>(null);

  const isLoading =
    isLoadingEmployee ||
    isLoadingUserRoles ||
    isLoadingDepartments ||
    isLoadingDesignations ||
    isLoadingDesignationAreas ||
    isLoadingEngagementStatus ||
    isLoadingRoles;

  const populateFormData = useCallback((employee: EmployeeWithProfile | null): FormDataShape => {
    const newFormData: FormDataShape = { ...(initialFormData as FormDataShape) };

    if (!employee) return newFormData;

    for (const key in newFormData) {
      if (key.startsWith('is')) {
        newFormData[key] = employee[key] ? 1 : 0;
      } else {
        const employeeValue = employee[key];
        if (
          typeof employeeValue === 'string' ||
          typeof employeeValue === 'number' ||
          typeof employeeValue === 'boolean' ||
          employeeValue === null ||
          employeeValue === undefined
        ) {
          newFormData[key] = employeeValue;
        }
      }
    }

    const joinDate = employee.joinDate as string | Date | null | undefined;

    newFormData.joinDate = (joinDate && getFormattedDate(joinDate, DATE_FORMAT_DEFAULT)) || '';

    newFormData.supervisorId = employee.supervisor?.id || null;
    newFormData.leaveIssuerId = employee.leaveIssuer?.id || null;
    newFormData.teamManagerId = employee.teamManager?.id || null;
    newFormData.coachId = employee.coach?.id || null;
    newFormData.departmentId = employee.department?.id || null;
    newFormData.username = employee.email || '';
    newFormData.roleIds = (employee.roles || [])
      .map((item) => (typeof item.id === 'number' ? item.id : Number(item.id)))
      .filter((value) => Number.isFinite(value));

    if (employee.pastExperience) {
      newFormData.pastExperienceYears = Math.floor((employee.pastExperience || 0) / 12);
      newFormData.pastExperienceMonths = (employee.pastExperience || 0) % 12;
    }

    newFormData.availabilityStartTime = {
      value: employee.availabilityTime?.startTime,
      type: employee.availabilityTime?.startTimeType,
    };

    newFormData.availabilityEndTime = {
      value: employee.availabilityTime?.endTime,
      type: employee.availabilityTime?.endTimeType,
    };

    newFormData.empStatusHistory = lodash.orderBy(
      employee.empStatusHistory,
      ['transitionDate', 'id'],
      ['desc', 'desc']
    );

    newFormData.designationAreaHistory = lodash.orderBy(
      employee.designationAreaHistory,
      ['transitionDate', 'id'],
      ['desc', 'desc']
    );

    if (employee.avatarUrl) {
      newFormData.employeeImage = [
        {
          data_url: employee.avatarUrl,
          file: { name: employee.avatarUrl.split('/').slice(-1)[0].split('?')[0] || '' },
        },
      ];
    }

    return newFormData;
  }, []);

  const getPageTitle = useCallback((employeeToShow: EmployeeWithProfile | null): string => {
    const employeeFullName = employeeToShow
      ? extractFullName({
          firstName: employeeToShow.firstName || '',
          middleName: employeeToShow.middleName || '',
          lastName: employeeToShow.lastName || '',
        })
      : '';
    return employeeFullName ? `${employeeFullName}'s profile edit | Vyaguta` : 'Profile edit | Vyaguta';
  }, []);

  const preparedForm = useMemo(() => {
    if (isLoading || !employeeData) {
      return null;
    }

    const userRoles = (userRolesResponse?.roles || []).map((role) => ({
      id: typeof role.id === 'number' ? role.id : Number(role.id),
      name: role.name,
    }));
    const rolesData: PermissionRole[] = allRoles.map((role) => ({ id: String(role.id), name: role.name }));
    const allowedRoles = filterAllowedRoles(rolesData, permissions).map((role) => ({
      id: Number(role.id),
      name: typeof role.name === 'string' ? role.name : '',
    }));

    return {
      selectedEmployee: employeeData,
      formData: populateFormData({ ...employeeData, roles: userRoles }),
      roles: lodash.uniqBy([...allowedRoles, ...userRoles], 'id'),
    };
  }, [allRoles, employeeData, isLoading, permissions, populateFormData, userRolesResponse]);

  useEffect(() => {
    if (preparedForm) {
      window.document.title = getPageTitle(preparedForm.selectedEmployee);
    }
  }, [getPageTitle, preparedForm]);

  const handleUpdateClick = useCallback(
    async (submittedFormData: UserPayloadInput) => {
      setIsSubmitting(true);

      const submittedData = submittedFormData as UserPayloadInput & {
        employeeImage?: File | EmployeeImagePreview[] | Array<{ file?: File }> | null;
      };
      const { employeeImage: employeeImageInput, ...updateFormData } = submittedData;
      const parsedEmployeeImage = employeeImageInput as File | EmployeeImagePreview[] | Array<{ file?: File }> | null;
      const employeeImage = Array.isArray(parsedEmployeeImage)
        ? parsedEmployeeImage[0]?.file || null
        : parsedEmployeeImage;

      const selectedEmployeeId = preparedForm?.selectedEmployee?.id;
      const userJson = {
        ...updateFormData,
        id: selectedEmployeeId,
        middleName: updateFormData.middleName || null,
      };

      try {
        let updatedUser: EmployeeWithProfile = {} as EmployeeWithProfile;

        const { data } = (await updateUserMutation.mutateAsync({
          user: userJson,
          id: userJson.id!,
        })) as { data: EmployeeWithProfile };

        updatedUser = { ...data };

        const updatedUserId = data?.id || null;

        if (updatedUserId && employeeImage instanceof File) {
          const imageUploadFormData = new FormData();
          imageUploadFormData.append('employeeImage', employeeImage);

          const { data: imageUploadData } = (await uploadUserImageMutation.mutateAsync({
            userId: updatedUserId,
            formData: imageUploadFormData,
          })) as {
            data: { avatarUrl?: string };
          };
          updatedUser.avatarUrl = imageUploadData.avatarUrl || null;
        }

        toast.success({
          title: 'Success',
          message: 'Profile has been updated successfully.',
        });

        setToPage({
          pageUrl: EMPLOYEE_PROFILE,
          params: {
            id: updatedUserId,
          },
        });

        const newEmployees = employees.reduce<EmployeeWithProfile[]>((acc, cur) => {
          if (cur.id !== userJson.id) {
            return [...acc, cur];
          }

          return [...acc, updatedUser];
        }, []);

        setEmployees(newEmployees);
      } catch (error) {
        handleError(error);
      }

      setIsSubmitting(false);
    },
    [employees, preparedForm, updateUserMutation, uploadUserImageMutation]
  );

  const canEdit = useCallback(
    (key: string): boolean => {
      const isSelf = +empId === user.id;
      const isSuperAdmin = permissions[USER_PERMISSIONS.CREATE_SUPER_ADMIN];

      if (key === 'roleIds') {
        const allowedRoleIds = filterAllowedRoles(
          (preparedForm?.roles || []).map((role) => ({
            id: String(role.id ?? ''),
            name: role.name || '',
          })),
          permissions
        ).reduce<Record<string, string>>(
          (prevValue, currentValue) => ({
            ...prevValue,
            [String(currentValue.id)]: String(currentValue.id),
          }),
          {}
        );

        return (
          isSuperAdmin ||
          (!isSelf && (preparedForm?.formData.roleIds || []).every((roleId) => allowedRoleIds[String(roleId)]))
        );
      }

      return isSelf && !isSuperAdmin ? USER_UPDATABLE_FIELDS.includes(key) : permissions[EMPLOYEES.PROFILE.UPDATE];
    },
    [empId, permissions, preparedForm, user.id]
  );

  if (toPage) {
    const togo = interpolate(toPage.pageUrl, toPage.params);
    return <Redirect to={togo} />;
  }

  return isLoading || !preparedForm ? (
    <div className="container loading-container">
      <Loading />
    </div>
  ) : (
    <div>
      {preparedForm.selectedEmployee && (
        <EmployeeForm
          isSubmitting={isSubmitting}
          formData={preparedForm.formData as EmployeeFormValues & FormDataShape}
          isCreateForm={false}
          canEdit={canEdit}
          handleSubmit={handleUpdateClick}
          departments={departments}
          designations={designations}
          designationAreas={designationAreas}
          engagementStatus={engagementStatus}
          roles={preparedForm.roles}
          empId={empId}
          employees={employees}
          skills={[]}
        />
      )}
    </div>
  );
};

export default UpdateForm;
